import { CATEGORY_SETUP, useEditor, type LightCategory, type Tool } from '../store/useEditor'
import {
  IconBush,
  IconCluster,
  IconColumn,
  IconFit,
  IconMeasure,
  IconMove,
  IconRoofline,
  IconSelect,
  IconSpark,
  IconSwag,
  IconTree,
  IconWreath,
} from './icons'

/**
 * Organized by what you are lighting, not by which drawing tool you are
 * holding. A contractor thinks "the roofline and the two royal palms", never
 * "a polyline and two spirals", and the navigation should match.
 */
const CATEGORIES: { id: LightCategory; icon: React.ReactNode }[] = [
  { id: 'roofline', icon: <IconRoofline /> },
  { id: 'garland', icon: <IconSwag /> },
  { id: 'trees', icon: <IconTree /> },
  { id: 'clusters', icon: <IconCluster /> },
  { id: 'wreaths', icon: <IconWreath /> },
  { id: 'columns', icon: <IconColumn /> },
  { id: 'other', icon: <IconBush /> },
]

const TOOLS: { id: Tool; label: string; icon: React.ReactNode }[] = [
  { id: 'select', label: 'Select', icon: <IconSelect /> },
  { id: 'pan', label: 'Move', icon: <IconMove /> },
  { id: 'calibrate', label: 'Measure', icon: <IconMeasure /> },
]

export default function LeftNav({ onFit }: { onFit: () => void }) {
  const category = useEditor((s) => s.category)
  const tool = useEditor((s) => s.tool)
  const setCategory = useEditor((s) => s.setCategory)
  const setTool = useEditor((s) => s.setTool)
  const aiAssist = useEditor((s) => s.aiAssist)
  const setAiAssist = useEditor((s) => s.setAiAssist)
  const aiReady = useEditor((s) => s.aiReady)

  // A category is only "current" while one of its own tools is active.
  const catActive = (id: LightCategory) => category === id && CATEGORY_SETUP[id].tool === tool

  return (
    <nav className="leftnav" aria-label="What to light">
      <div className="nav-group">
        <h2>Add lights</h2>

        {/* A mode across every category, not a setting inside one of them. */}
        <div className="ai-switch" role="group" aria-label="How to draw">
          <button
            className={aiAssist ? 'ai-opt on' : 'ai-opt'}
            onClick={() => setAiAssist(true)}
            aria-pressed={aiAssist}
          >
            <IconSpark size={15} />
            AI Assist
          </button>
          <button
            className={!aiAssist ? 'ai-opt on' : 'ai-opt'}
            onClick={() => setAiAssist(false)}
            aria-pressed={!aiAssist}
          >
            Manual
          </button>
        </div>

        <ul>
          {CATEGORIES.map((c) => (
            <li key={c.id}>
              <button
                className={catActive(c.id) ? 'nav-item on' : 'nav-item'}
                onClick={() => setCategory(c.id)}
                aria-current={catActive(c.id) || undefined}
              >
                <span className="ni-icon">{c.icon}</span>
                <span className="ni-label">{CATEGORY_SETUP[c.id].label}</span>
                {aiAssist && aiReady(c.id) && <span className="ni-ai" title="AI can do this one" />}
              </button>
            </li>
          ))}
        </ul>
      </div>

      <div className="nav-sep" />

      <div className="nav-group">
        <h2>Tools</h2>
        <ul>
          {TOOLS.map((t) => (
            <li key={t.id}>
              <button
                className={tool === t.id ? 'nav-item on' : 'nav-item'}
                onClick={() => setTool(t.id)}
                aria-current={tool === t.id || undefined}
              >
                <span className="ni-icon">{t.icon}</span>
                <span className="ni-label">{t.label}</span>
              </button>
            </li>
          ))}
        </ul>
      </div>

      <div className="nav-sep" />

      <div className="nav-group">
        <h2>View</h2>
        <ul>
          <li>
            <button className="nav-item" onClick={onFit}>
              <span className="ni-icon">
                <IconFit />
              </span>
              <span className="ni-label">Fit to view</span>
            </button>
          </li>
        </ul>
      </div>
    </nav>
  )
}
